import { BlockBaseSchema, BlockIdSchema } from "blocks/types/base";
import { z } from "zod";
import { blockType } from "./constants";

export const FieldBlockConfigBaseSchema = z.object({
  id: BlockIdSchema.optional(),
  name: z.string(),
  nameAutoGenerated: z.boolean().default(false).optional(),
  required: z.boolean(),
  hidden: z.boolean().optional(),
  label: z.string(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.string().optional(),
  tablesFieldKey: z.string().optional(),
});

export const FieldBlockStandardConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z.union([
      z.literal("text"),
      z.literal("textarea"),
      z.literal("url"),
    ]),
  }
);

export const FieldBlockDropDownConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z.literal("dropdown"),
    options: z.string().optional(),
    multiSelect: z.boolean().optional(),
    tableId: z.string().optional(),
    tableFieldId: z.number().or(z.string()).optional(),
  }
);

export const FieldBlockNumberConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z.literal("number"),
  min: z.string().optional(),
  max: z.string().optional(),
});

export const FieldBlockYesNoConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z.literal("yes-no"),
});

export const FieldBlockEmailConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z.literal("email"),
  blockFreeEmailProviders: z.boolean().optional(),
  restrictDomains: z.boolean().optional(),
  allowedDomains: z.string().optional(),
});

export const DateFormatOptions = [
  {
    label: "YYYY-MM-DD",
    value: "yyyy-MM-dd",
  },
  {
    label: "MM-DD-YYYY",
    value: "MM-dd-yyyy",
  },
  {
    label: "DD-MM-YYYY",
    value: "dd-MM-yyyy",
  },
];

export const FieldBlockDatepickerConfigSchema =
  FieldBlockConfigBaseSchema.extend({
    inputType: z.literal("date-picker"),
    dateFormat: z.string().optional(),
    includeTime: z.boolean().default(false),
    defaultToNow: z.boolean().default(false),
  });

export const PhoneNumberFormatOptions = [
  {
    label: "(555) 555-1212",
    value: "phone_number_format_1",
  },
  {
    label: "+1 (555) 555-1212",
    value: "phone_number_format_2",
  },
  {
    label: "+44 55 1234 5678",
    value: "phone_number_format_3",
  },
];

export const FieldBlockPhoneNumberConfigSchema =
  FieldBlockConfigBaseSchema.extend({
    inputType: z.literal("phone-number"),
    phoneNumberFormat: z.string().default("phone_number_format_1"),
    defaultCountry: z.string().length(2).optional().default("US"),
  });

export const FieldBlockCheckboxConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z.literal("checkbox"),
    label: z.string().optional().default(""),
    text: z.string(),
    defaultChecked: z.boolean().default(false),
  }
);

export const FieldBlockFileUploadSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z.literal("file-upload"),
  allowedFileTypes: z.string().optional(),
  allowedFileSize: z.number().optional(),
});

export const CurrencyFormatOptions = [
  {
    label: "Default ($20.11)",
    value: "usd1",
  },
  {
    label: "USD ($20.11)",
    value: "usd2",
  },
  {
    label: "Euro (20.11€)",
    value: "euro1",
  },
  {
    label: "Pound (£20.11)",
    value: "pound1",
  },
  {
    label: "Yen (¥20.11)",
    value: "yen1",
  },
] as const;

export const CurrencyDecimalOptions = [
  {
    label: "00",
    value: 0,
  },
  {
    label: "0.00",
    value: 2,
  },
] as const;

export const FieldBlockCurrencyConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z.literal("currency"),
    decimals: z.number().default(CurrencyDecimalOptions[0].value),
    currencyFormat: z.string().default(CurrencyFormatOptions[0].value),
  }
);

export const FieldBlockConfigSchema = z.union([
  FieldBlockStandardConfigSchema,
  FieldBlockDropDownConfigSchema,
  FieldBlockNumberConfigSchema,
  FieldBlockYesNoConfigSchema,
  FieldBlockEmailConfigSchema,
  FieldBlockDatepickerConfigSchema,
  FieldBlockPhoneNumberConfigSchema,
  FieldBlockCheckboxConfigSchema,
  FieldBlockFileUploadSchema,
  FieldBlockCurrencyConfigSchema,
]);

export const FieldBlockSchema = BlockBaseSchema.extend({
  type: z.literal(blockType),
  config: FieldBlockConfigSchema,
}).openapi({ ref: "FieldBlock" });
